import { useState, useEffect, useCallback, useRef } from 'react';
import { QueryDocumentSnapshot, DocumentData } from 'firebase/firestore';
import { useDebouncedCallback } from 'use-debounce';
import { Order, OrderStatus } from '../types';
import { OrderSortField, OrderSortOrder } from '../components/OrderSort';
import { 
  fetchOrdersPaginated, 
  getOrderCount, 
  PaginatedOrdersResult,
  searchOrders
} from '../services/orderService';

interface UsePaginatedOrdersOptions {
  pageSize?: number;
  enabled?: boolean;
}

interface SearchOrdersResult {
  orders: Order[];
  total: number;
}

interface UsePaginatedOrdersResult {
  orders: Order[];
  loading: boolean;
  error: string | null;
  totalCount: number;
  currentPage: number;
  totalPages: number;
  hasMore: boolean;
  searchQuery: string;
  selectedStatuses: OrderStatus[];
  selectedPaymentStatuses: string[];
  selectedShippingMethods: string[];
  selectedDiscountCodes: string[];
  selectedPackedStatuses: string[];
  priorityProcessingFilter: boolean;
  sortField: OrderSortField | null;
  sortOrder: OrderSortOrder;
  isSearchMode: boolean;
  isFilterMode: boolean;
  isSortMode: boolean;
  isCombinedMode: boolean;
  searchLoading: boolean;
  filterLoading: boolean;
  sortLoading: boolean;
  setSearchQuery: (query: string) => void;
  setSelectedStatuses: (statuses: OrderStatus[]) => void;
  setSelectedPaymentStatuses: (statuses: string[]) => void;
  setSelectedShippingMethods: (methods: string[]) => void;
  setSelectedDiscountCodes: (codes: string[]) => void;
  setSelectedPackedStatuses: (statuses: string[]) => void;
  setPriorityProcessingFilter: (enabled: boolean) => void;
  setSorting: (field: OrderSortField | null, order: OrderSortOrder) => void;
  handleSearch: (query: string) => void;
  fetchNextPage: () => Promise<void>;
  fetchPage: (page: number) => Promise<void>;
  refresh: () => Promise<void>;
  reset: () => void;
  updateOrderInCache: (updatedOrder: Order) => void;
  removeOrderFromCache: (orderId: string) => void;
  invalidateCache: () => void;
}

/**
 * Hook for paginated order fetching with search and filtering
 * Matches the products page architecture exactly
 */
export const usePaginatedOrders = ({ 
  pageSize = 20,
  enabled = true 
}: UsePaginatedOrdersOptions = {}): UsePaginatedOrdersResult => {
  // Server orders state (for browse mode)
  const [orders, setOrders] = useState<Order[]>([]);
  const [serverOrders, setServerOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  
  // Search state management
  const [isSearchMode, setIsSearchMode] = useState(false);
  const [searchCache, setSearchCache] = useState<Map<string, SearchOrdersResult>>(new Map());
  const [searchResults, setSearchResults] = useState<Order[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchCurrentPage, setSearchCurrentPage] = useState(1);
  
  // Filter state management  
  const [isFilterMode, setIsFilterMode] = useState(false);
  const [filterCache, setFilterCache] = useState<Map<string, SearchOrdersResult>>(new Map());
  const [filterResults, setFilterResults] = useState<Order[]>([]);
  const [filterLoading, setFilterLoading] = useState(false);
  const [filterCurrentPage, setFilterCurrentPage] = useState(1);
  
  // Sort state management
  const [isSortMode, setIsSortMode] = useState(false);
  const [sortCache, setSortCache] = useState<Map<string, SearchOrdersResult>>(new Map());
  const [sortResults, setSortResults] = useState<Order[]>([]);
  const [sortLoading, setSortLoading] = useState(false);
  const [sortCurrentPage, setSortCurrentPage] = useState(1);
  
  // Combined mode state (when search, filters, and/or sorting are active)
  const [combinedResults, setCombinedResults] = useState<Order[]>([]);
  const [combinedCurrentPage, setCombinedCurrentPage] = useState(1);
  
  // Computed state for combined mode (any combination of search, filter, sort)
  const isCombinedMode = (isSearchMode && isFilterMode) || (isSearchMode && isSortMode) || (isFilterMode && isSortMode) || (isSearchMode && isFilterMode && isSortMode);
  
  // Filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedStatuses, setSelectedStatuses] = useState<OrderStatus[]>([]);
  const [selectedPaymentStatuses, setSelectedPaymentStatuses] = useState<string[]>([]);
  const [selectedShippingMethods, setSelectedShippingMethods] = useState<string[]>([]);
  const [selectedDiscountCodes, setSelectedDiscountCodes] = useState<string[]>([]);
  const [selectedPackedStatuses, setSelectedPackedStatuses] = useState<string[]>([]);
  const [priorityProcessingFilter, setPriorityProcessingFilter] = useState(false);
  
  // Sort state
  const [sortField, setSortField] = useState<OrderSortField | null>(null);
  const [sortOrder, setSortOrder] = useState<OrderSortOrder>('desc');
  
  // Use ref for pagination cursors to avoid dependency issues
  const pageCursorsRef = useRef<Map<number, QueryDocumentSnapshot<DocumentData> | null>>(new Map());
  
  const totalPages = Math.ceil(totalCount / pageSize);

  // Fetch all orders for search (with caching)
  const fetchOrdersForSearch = useCallback(async (): Promise<Order[]> => {
    const cacheKey = 'search_all_orders';
    
    // Check cache first
    const cached = searchCache.get(cacheKey);
    if (cached) {
      console.log('Using cached search data for all orders');
      return cached.orders;
    }

    try {
      setSearchLoading(true);
      setError(null);
      
      // Fetch all orders once, apply status filtering client-side
      const result = await searchOrders('', undefined);
      
      // Cache the result
      setSearchCache(prev => new Map(prev).set(cacheKey, result));
      
      return result.orders;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch orders for search';
      setError(errorMessage);
      console.error('Search fetch error:', error);
      return [];
    } finally {
      setSearchLoading(false);
    }
  }, [searchCache]);

  // Fetch all orders for filtering (with caching)
  const fetchOrdersForFilter = useCallback(async (): Promise<Order[]> => {
    const cacheKey = 'filter_all_orders';
    
    // Check cache first
    const cached = filterCache.get(cacheKey);
    if (cached) {
      console.log('Using cached filter data for all orders');
      return cached.orders;
    }

    try {
      setFilterLoading(true);
      setError(null);
      
      // Fetch all orders once, apply status filtering client-side
      const result = await searchOrders('', undefined);
      
      // Cache the result
      setFilterCache(prev => new Map(prev).set(cacheKey, result));
      
      return result.orders;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch orders for filtering';
      setError(errorMessage);
      console.error('Filter fetch error:', error);
      return [];
    } finally {
      setFilterLoading(false);
    }
  }, [filterCache]);

  // Fetch all orders for sorting (with caching)
  const fetchOrdersForSort = useCallback(async (): Promise<Order[]> => {
    const cacheKey = 'sort_all_orders';
    
    // Check cache first
    const cached = sortCache.get(cacheKey);
    if (cached) {
      console.log('Using cached sort data for all orders');
      return cached.orders;
    }

    try {
      setSortLoading(true);
      setError(null);
      
      // Fetch all orders once, apply status filtering client-side
      const result = await searchOrders('', undefined);
      
      // Cache the result
      setSortCache(prev => new Map(prev).set(cacheKey, result));
      
      return result.orders;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch orders for sorting';
      setError(errorMessage);
      console.error('Sort fetch error:', error);
      return [];
    } finally {
      setSortLoading(false);
    }
  }, [sortCache]);

  // Apply client-side filters (status, payment status, shipping method, discount codes, packed status, and priority processing)
  const applyFilters = useCallback((orders: Order[]): Order[] => {
    let filteredOrders = orders;
    
    // Apply status filters
    if (selectedStatuses.length > 0) {
      filteredOrders = filteredOrders.filter(order => selectedStatuses.includes(order.status));
    }
    
    // Apply payment status filters
    if (selectedPaymentStatuses.length > 0) {
      filteredOrders = filteredOrders.filter(order => {
        const paymentStatus = order.payment?.status || 'pending';
        return selectedPaymentStatuses.includes(paymentStatus);
      });
    }
    
    // Apply shipping method filters
    if (selectedShippingMethods.length > 0) {
      filteredOrders = filteredOrders.filter(order => {
        const shippingMethod = order.shipping?.option || order.shipping?.method || '';
        return selectedShippingMethods.includes(shippingMethod);
      });
    }
    
    // Apply discount code filters
    if (selectedDiscountCodes.length > 0) {
      filteredOrders = filteredOrders.filter(order => {
        const discountCode = order.discount?.code;
        return discountCode && selectedDiscountCodes.includes(discountCode);
      });
    }
    
    // Apply packed status filters
    if (selectedPackedStatuses.length > 0) {
      filteredOrders = filteredOrders.filter(order => {
        const packedStatus = order.packed ? 'packed' : 'unpacked';
        return selectedPackedStatuses.includes(packedStatus);
      });
    }
    
    // Apply priority processing filter
    if (priorityProcessingFilter) {
      filteredOrders = filteredOrders.filter(order => order.shipping?.priorityProcessing === true);
    }
    
    return filteredOrders;
  }, [selectedStatuses, selectedPaymentStatuses, selectedShippingMethods, selectedDiscountCodes, selectedPackedStatuses, priorityProcessingFilter]);

  // Sort utility functions
  const getOrderValue = useCallback((order: Order, field: OrderSortField): number => {
    switch (field) {
      case 'createdAt':
        if (order.createdAt instanceof Date) {
          return order.createdAt.getTime();
        } else if (typeof order.createdAt === 'string') {
          return new Date(order.createdAt).getTime();
        }
        return 0;
      case 'updatedAt':
        if (order.updatedAt instanceof Date) {
          return order.updatedAt.getTime();
        } else if (typeof order.updatedAt === 'string') {
          return new Date(order.updatedAt).getTime();
        }
        return 0;
      case 'total':
        return order.total;
      case 'status':
        // Convert status to number for sorting
        const statusOrder = { 'pending': 1, 'paid': 2, 'shipped': 3, 'delivered': 4, 'cancelled': 5 };
        return statusOrder[order.status as keyof typeof statusOrder] || 0;
      case 'paymentStatus':
        // Convert payment status to number for sorting
        const paymentStatus = order.payment?.status || 'pending';
        const paymentOrder = { 'pending': 1, 'succeeded': 2, 'failed': 3, 'refund_pending': 4, 'refunded': 5 };
        return paymentOrder[paymentStatus as keyof typeof paymentOrder] || 0;
      case 'customer':
        return order.customer.name.toLowerCase().charCodeAt(0);
      default:
        return 0;
    }
  }, []);

  // Apply sorting to orders
  const applySorting = useCallback((orders: Order[], field: OrderSortField, order: OrderSortOrder): Order[] => {
    return [...orders].sort((a, b) => {
      if (field === 'customer') {
        const aName = a.customer.name.toLowerCase();
        const bName = b.customer.name.toLowerCase();
        return order === 'asc' ? aName.localeCompare(bName) : bName.localeCompare(aName);
      }
      
      const aValue = getOrderValue(a, field);
      const bValue = getOrderValue(b, field);
      
      return order === 'asc' ? aValue - bValue : bValue - aValue;
    });
  }, [getOrderValue]);

  // Check if sorting is active
  const hasSorting = useCallback((): boolean => {
    return sortField !== null;
  }, [sortField]);

  // Debounced sort handler
  const debouncedSort = useDebouncedCallback(async () => {
    if (!hasSorting()) {
      // No sorting active - exit sort mode
      setIsSortMode(false);
      setSortResults([]);
      setSortCurrentPage(1);
      setCombinedResults([]);
      setCombinedCurrentPage(1);
      
      // If no search/filters active and no server orders, load the current page
      if (!isSearchMode && !isFilterMode && serverOrders.length === 0 && !loading) {
        await loadPage(currentPage);
      }
      
      return;
    }

    // Enter sort mode
    setIsSortMode(true);
    setSortCurrentPage(1);
    setCombinedCurrentPage(1);
    
    if (isSearchMode && isFilterMode) {
      // Sort combined search + filter results
      const results = applySorting(combinedResults, sortField!, sortOrder);
      setCombinedResults(results);
    } else if (isSearchMode) {
      // Sort search results
      const results = applySorting(searchResults, sortField!, sortOrder);
      setCombinedResults(results);
    } else if (isFilterMode) {
      // Sort filter results
      const results = applySorting(filterResults, sortField!, sortOrder);
      setCombinedResults(results);
    } else {
      // Sort all orders
      const allOrders = await fetchOrdersForSort();
      const results = applySorting(allOrders, sortField!, sortOrder);
      setSortResults(results);
    }
  }, 300);

  // Perform client-side search
  const performSearch = useCallback((query: string, orders: Order[]): Order[] => {
    if (!query.trim()) return orders;
    
    const searchLower = query.toLowerCase();
    return orders.filter(order => {
      // Search by order ID
      if (order.id.toLowerCase().includes(searchLower)) {
        return true;
      }
      
      // Search by order number (if different from ID)
      if (order.orderNumber && order.orderNumber.toLowerCase().includes(searchLower)) {
        return true;
      }
      
      // Search by tracking number
      if (order.tracking?.trackingNumber && order.tracking.trackingNumber.toLowerCase().includes(searchLower)) {
        return true;
      }
      
      // Search by customer name
      if (order.customer.name.toLowerCase().includes(searchLower)) {
        return true;
      }
      
      // Search by customer email
      if (order.customer.email.toLowerCase().includes(searchLower)) {
        return true;
      }
      
      // Search by customer address
      if (order.customer.address) {
        const addr = order.customer.address;
        const fullAddress = `${addr.line1} ${addr.line2 || ''} ${addr.city} ${addr.state} ${addr.postal_code}`.toLowerCase();
        if (fullAddress.includes(searchLower)) {
          return true;
        }
      }
      
      // Search by customer addresses array (fallback for legacy structure)
      if (order.customer.addresses && order.customer.addresses.length > 0) {
        for (const addr of order.customer.addresses) {
          const fullAddress = `${addr.line1 || ''} ${addr.line2 || ''} ${addr.city} ${addr.state} ${addr.zipCode}`.toLowerCase();
          if (fullAddress.includes(searchLower)) {
            return true;
          }
        }
      }
      
      return false;
    });
  }, []);

  // Check if any filters are active
  const hasActiveFilters = useCallback((): boolean => {
    return selectedStatuses.length > 0 || selectedPaymentStatuses.length > 0 || selectedShippingMethods.length > 0 || selectedDiscountCodes.length > 0 || selectedPackedStatuses.length > 0 || priorityProcessingFilter;
  }, [selectedStatuses, selectedPaymentStatuses, selectedShippingMethods, selectedDiscountCodes, selectedPackedStatuses, priorityProcessingFilter]);

  // Load total count
  const loadTotalCount = useCallback(async () => {
    try {
      const count = await getOrderCount(undefined, undefined);
      setTotalCount(count);
    } catch (err) {
      console.error('Error fetching order count:', err);
      setError('Failed to load order count');
    }
  }, []);

  // Load orders for a specific page (browse mode only)
  const loadPage = useCallback(async (page: number) => {
    if (!enabled) return;
    
    try {
      setLoading(true);
      setError(null);
      
      // Regular pagination - no search query
      let cursor: QueryDocumentSnapshot<DocumentData> | null = null;
      
      if (page > 1) {
        cursor = pageCursorsRef.current.get(page - 1) || null;
        
        // If we don't have the cursor, we need to fetch from the beginning
        if (!cursor) {
          // Reset and fetch sequentially
          pageCursorsRef.current.clear();
          let currentCursor: QueryDocumentSnapshot<DocumentData> | null = null;
          
          for (let p = 1; p < page; p++) {
            const result = await fetchOrdersPaginated(
              pageSize,
              currentCursor,
              undefined, // No search query for regular pagination
              undefined // No status filter for regular pagination in browse mode
            );
            
            if (result.lastDoc) {
              pageCursorsRef.current.set(p, result.lastDoc);
              currentCursor = result.lastDoc;
            }
          }
          
          cursor = currentCursor;
        }
      }
      
      const result: PaginatedOrdersResult = await fetchOrdersPaginated(
        pageSize,
        cursor,
        undefined, // No search query for regular pagination
        undefined // No status filter for regular pagination in browse mode
      );
      
      setServerOrders(result.orders);
      setHasMore(result.hasMore);
      setCurrentPage(page);
      
      // Store the cursor for the next page
      if (result.lastDoc) {
        pageCursorsRef.current.set(page, result.lastDoc);
      }
      
    } catch (err) {
      console.error('Error fetching orders:', err);
      setError('Failed to load orders');
      setServerOrders([]);
    } finally {
      setLoading(false);
    }
  }, [enabled, pageSize]);

  // Debounced filter handler
  const debouncedFilter = useDebouncedCallback(async () => {
    if (!hasActiveFilters()) {
      // No filters active - exit filter mode
      setIsFilterMode(false);
      setFilterResults([]);
      setFilterCurrentPage(1);
      setCombinedResults([]);
      setCombinedCurrentPage(1);
      
      // If no search is active and no server orders, load the current page
      if (!isSearchMode && serverOrders.length === 0 && !loading) {
        await loadPage(currentPage);
      }
      
      return;
    }

    // Enter filter mode
    setIsFilterMode(true);
    setFilterCurrentPage(1);
    setCombinedCurrentPage(1);
    
    if (isSearchMode && isSortMode) {
      // Search + Filter + Sort: Apply filters to search results, then sort
      const filtered = applyFilters(searchResults);
      const sorted = applySorting(filtered, sortField!, sortOrder);
      setCombinedResults(sorted);
    } else if (isSearchMode) {
      // Search + Filter: Apply filters to search results
      const results = applyFilters(searchResults);
      setCombinedResults(results);
    } else if (isSortMode) {
      // Filter + Sort: Fetch all orders, apply filters, then sort
      const allOrders = await fetchOrdersForFilter();
      const filtered = applyFilters(allOrders);
      const sorted = applySorting(filtered, sortField!, sortOrder);
      setCombinedResults(sorted);
    } else {
      // Filter only: Fetch all orders and apply filters
      const allOrders = await fetchOrdersForFilter();
      const results = applyFilters(allOrders);
      setFilterResults(results);
    }
  }, 300);

  // Debounced search handler
  const debouncedSearch = useDebouncedCallback(async (query: string) => {
    if (!query.trim()) {
      // Empty search - exit search mode
      setIsSearchMode(false);
      setSearchResults([]);
      setSearchCurrentPage(1);
      setCombinedResults([]);
      setCombinedCurrentPage(1);
      
      // If no filters are active and no server orders, load the current page
      if (!hasActiveFilters() && serverOrders.length === 0 && !loading) {
        await loadPage(currentPage);
      }
      
      return;
    }

    // Enter search mode and fetch all orders if needed
    setIsSearchMode(true);
    setSearchCurrentPage(1);
    setCombinedCurrentPage(1);
    
    const allOrders = await fetchOrdersForSearch();
    const searchFiltered = performSearch(query, allOrders);
    
    setSearchResults(searchFiltered);
    
    // Apply filters if active
    if (hasActiveFilters()) {
      // Search + Filter
      const statusFiltered = applyFilters(searchFiltered);
      setCombinedResults(statusFiltered);
    }
  }, 300);

  // Fetch next page
  const fetchNextPage = useCallback(async () => {
    if (!hasMore || loading) return;
    await loadPage(currentPage + 1);
  }, [hasMore, loading, currentPage, loadPage]);

  // Fetch specific page
  const fetchPage = useCallback(async (page: number) => {
    if (page < 1 || page > totalPages) return;
    
    if (isCombinedMode) {
      // Combined mode: client-side pagination
      setCombinedCurrentPage(page);
      setCurrentPage(page);
    } else if (isSearchMode) {
      // Search mode: client-side pagination
      setSearchCurrentPage(page);
      setCurrentPage(page);
    } else if (isFilterMode) {
      // Filter mode: client-side pagination
      setFilterCurrentPage(page);
      setCurrentPage(page);
    } else {
      // Browse mode: server-side pagination
      await loadPage(page);
    }
  }, [totalPages, isCombinedMode, isSearchMode, isFilterMode, loadPage]);

  // Refresh current page
  const refresh = useCallback(async () => {
    // Clear cursors and reload current page
    pageCursorsRef.current.clear();
    setSearchCache(new Map());
    setFilterCache(new Map());
    await loadTotalCount();
    await loadPage(currentPage);
  }, [loadPage, loadTotalCount, currentPage]);

  // Reset pagination
  const reset = useCallback(() => {
    setOrders([]);
    setServerOrders([]);
    setCurrentPage(1);
    pageCursorsRef.current.clear();
    setHasMore(false);
    setError(null);
    setSearchQuery('');
    setSelectedStatuses([]);
    setSelectedPaymentStatuses([]);
    setSelectedShippingMethods([]);
    setSelectedDiscountCodes([]);
    setSelectedPackedStatuses([]);
    setPriorityProcessingFilter(false);
    setIsSearchMode(false);
    setSearchResults([]);
    setSearchCurrentPage(1);
    setSearchCache(new Map());
    setIsFilterMode(false);
    setFilterResults([]);
    setFilterCurrentPage(1);
    setFilterCache(new Map());
    setIsSortMode(false);
    setSortResults([]);
    setSortCurrentPage(1);
    setSortCache(new Map());
    setCombinedResults([]);
    setCombinedCurrentPage(1);
    setSortField(null);
    setSortOrder('desc');
  }, []);

  // Invalidate all caches
  const invalidateCache = useCallback(() => {
    console.log('Invalidating all caches');
    setSearchCache(new Map());
    setFilterCache(new Map());
    setSortCache(new Map());
    setSearchResults([]);
    setFilterResults([]);
    setSortResults([]);
    setCombinedResults([]);
  }, []);

  // Handle search query changes
  const handleSetSearchQuery = useCallback((query: string) => {
    setSearchQuery(query);
    setCurrentPage(1);
    pageCursorsRef.current.clear();
  }, []);

  // Handle status changes
  const handleSetSelectedStatuses = useCallback((statuses: OrderStatus[]) => {
    setSelectedStatuses(statuses);
    setCurrentPage(1);
    pageCursorsRef.current.clear();
    // Trigger filter mode check - no need to clear caches since we're doing client-side filtering
    setTimeout(() => debouncedFilter(), 0);
  }, [debouncedFilter]);

  // Handle payment status changes
  const handleSetSelectedPaymentStatuses = useCallback((statuses: string[]) => {
    setSelectedPaymentStatuses(statuses);
    setCurrentPage(1);
    pageCursorsRef.current.clear();
    // Trigger filter mode check - no need to clear caches since we're doing client-side filtering
    setTimeout(() => debouncedFilter(), 0);
  }, [debouncedFilter]);

  // Handle shipping method changes
  const handleSetSelectedShippingMethods = useCallback((methods: string[]) => {
    setSelectedShippingMethods(methods);
    setCurrentPage(1);
    pageCursorsRef.current.clear();
    // Trigger filter mode check - no need to clear caches since we're doing client-side filtering
    setTimeout(() => debouncedFilter(), 0);
  }, [debouncedFilter]);

  // Handle discount code changes
  const handleSetSelectedDiscountCodes = useCallback((codes: string[]) => {
    setSelectedDiscountCodes(codes);
    setCurrentPage(1);
    pageCursorsRef.current.clear();
    // Trigger filter mode check - no need to clear caches since we're doing client-side filtering
    setTimeout(() => debouncedFilter(), 0);
  }, [debouncedFilter]);

  // Handle packed status changes
  const handleSetSelectedPackedStatuses = useCallback((statuses: string[]) => {
    setSelectedPackedStatuses(statuses);
    setCurrentPage(1);
    pageCursorsRef.current.clear();
    // Trigger filter mode check - no need to clear caches since we're doing client-side filtering
    setTimeout(() => debouncedFilter(), 0);
  }, [debouncedFilter]);

  // Handle priority processing filter changes
  const handleSetPriorityProcessingFilter = useCallback((enabled: boolean) => {
    setPriorityProcessingFilter(enabled);
    setCurrentPage(1);
    pageCursorsRef.current.clear();
    // Trigger filter mode check - no need to clear caches since we're doing client-side filtering
    setTimeout(() => debouncedFilter(), 0);
  }, [debouncedFilter]);

  // Handle sorting changes
  const handleSetSorting = useCallback((field: OrderSortField | null, order: OrderSortOrder) => {
    setSortField(field);
    setSortOrder(order);
    setCurrentPage(1);
    pageCursorsRef.current.clear();
    // Trigger sort handler
    setTimeout(() => debouncedSort(), 0);
  }, [debouncedSort]);

  // Handle search with debouncing
  const handleSearch = useCallback((query: string) => {
    setSearchQuery(query);
    debouncedSearch(query);
  }, [debouncedSearch]);

  // Get paginated search results
  const getPaginatedSearchResults = useCallback((): Order[] => {
    if (!isSearchMode) return [];
    
    const startIndex = (searchCurrentPage - 1) * pageSize;
    const endIndex = Math.min(startIndex + pageSize, searchResults.length);
    return searchResults.slice(startIndex, endIndex);
  }, [isSearchMode, searchCurrentPage, pageSize, searchResults]);

  // Get paginated filter results
  const getPaginatedFilterResults = useCallback((): Order[] => {
    if (!isFilterMode) return [];
    
    const startIndex = (filterCurrentPage - 1) * pageSize;
    const endIndex = Math.min(startIndex + pageSize, filterResults.length);
    return filterResults.slice(startIndex, endIndex);
  }, [isFilterMode, filterCurrentPage, pageSize, filterResults]);

  // Get paginated sort results
  const getPaginatedSortResults = useCallback((): Order[] => {
    if (!isSortMode) return [];
    
    const startIndex = (sortCurrentPage - 1) * pageSize;
    const endIndex = Math.min(startIndex + pageSize, sortResults.length);
    return sortResults.slice(startIndex, endIndex);
  }, [isSortMode, sortCurrentPage, pageSize, sortResults]);

  // Get paginated combined results (search + filter)
  const getPaginatedCombinedResults = useCallback((): Order[] => {
    if (!isCombinedMode) return [];
    
    const startIndex = (combinedCurrentPage - 1) * pageSize;
    const endIndex = Math.min(startIndex + pageSize, combinedResults.length);
    return combinedResults.slice(startIndex, endIndex);
  }, [isCombinedMode, combinedCurrentPage, pageSize, combinedResults]);

  // Apply filters and update final orders
  useEffect(() => {
    if (loading && !isSearchMode && !isFilterMode && !isSortMode) return;

    // Determine which orders to use based on mode
    let sourceOrders: Order[];
    if (isCombinedMode) {
      sourceOrders = getPaginatedCombinedResults();
    } else if (isSearchMode) {
      sourceOrders = getPaginatedSearchResults();
    } else if (isFilterMode) {
      sourceOrders = getPaginatedFilterResults();
    } else if (isSortMode) {
      sourceOrders = getPaginatedSortResults();
    } else {
      sourceOrders = serverOrders;
    }

    setOrders(sourceOrders);

    // Update total count based on mode
    if (isCombinedMode) {
      setTotalCount(combinedResults.length);
    } else if (isSearchMode) {
      setTotalCount(searchResults.length);
    } else if (isFilterMode) {
      setTotalCount(filterResults.length);
    } else if (isSortMode) {
      setTotalCount(sortResults.length);
    } else {
      // In browse mode, reload the total count to get accurate count
      loadTotalCount();
    }
  }, [
    serverOrders,
    searchResults,
    filterResults,
    sortResults,
    combinedResults,
    searchCurrentPage,
    filterCurrentPage,
    sortCurrentPage,
    combinedCurrentPage,
    isSearchMode,
    isFilterMode,
    isSortMode,
    isCombinedMode,
    loading,
    getPaginatedSearchResults,
    getPaginatedFilterResults,
    getPaginatedSortResults,
    getPaginatedCombinedResults,
    loadTotalCount
  ]);

  // Initial load and when filters change
  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return;
    }

    const initialize = async () => {
      // Reset pagination state when filters change
      setCurrentPage(1);
      pageCursorsRef.current.clear();
      setHasMore(false);
      setError(null);
      
      // Load total count and first page
      await loadTotalCount();
      await loadPage(1);
    };

    initialize();
  }, [enabled, loadTotalCount, loadPage]);

  // Update order in cache
  const updateOrderInCache = useCallback((updatedOrder: Order) => {
    console.log(`Updating order ${updatedOrder.id} in caches`);
    
    // Helper function to update an order in an array
    const updateOrderInArray = (orders: Order[]) => 
      orders.map(order => order.id === updatedOrder.id ? updatedOrder : order);
    
    // Helper function to update cache maps
    const updateCacheMap = (cacheMap: Map<string, SearchOrdersResult>) => {
      const newCache = new Map(cacheMap);
      for (const [key, cachedResult] of newCache.entries()) {
        const updatedOrders = updateOrderInArray(cachedResult.orders);
        newCache.set(key, { ...cachedResult, orders: updatedOrders });
      }
      return newCache;
    };
    
    // Helper function to calculate and update pagination
    const updatePaginationView = (results: Order[], currentPage: number) => {
      const startIndex = (currentPage - 1) * pageSize;
      const endIndex = Math.min(startIndex + pageSize, results.length);
      setOrders(results.slice(startIndex, endIndex));
      setTotalCount(results.length);
    };
    
    // Calculate all updated values immediately
    const updatedSearchResults = updateOrderInArray(searchResults);
    const updatedFilterResults = updateOrderInArray(filterResults);
    const updatedSortResults = updateOrderInArray(sortResults);
    const updatedCombinedResults = updateOrderInArray(combinedResults);
    const updatedSearchCache = updateCacheMap(searchCache);
    const updatedFilterCache = updateCacheMap(filterCache);
    const updatedSortCache = updateCacheMap(sortCache);
    
    // Update all order arrays with the calculated values
    setOrders(prev => updateOrderInArray(prev));
    setServerOrders(prev => updateOrderInArray(prev));
    setSearchResults(updatedSearchResults);
    setFilterResults(updatedFilterResults);
    setSortResults(updatedSortResults);
    setCombinedResults(updatedCombinedResults);
    
    // Update all cache maps
    setSearchCache(updatedSearchCache);
    setFilterCache(updatedFilterCache);
    setSortCache(updatedSortCache);
    
    // Re-apply operations and update displayed orders using fresh data
    const updateDisplayedOrders = async () => {
      if (isSearchMode && hasActiveFilters() && hasSorting()) {
        // Re-apply filters and sorting to fresh search results
        const filtered = applyFilters(updatedSearchResults);
        const sorted = applySorting(filtered, sortField!, sortOrder);
        setCombinedResults(sorted);
        updatePaginationView(sorted, combinedCurrentPage);
        
      } else if (isSearchMode && hasActiveFilters()) {
        // Re-apply filters to fresh search results
        const results = applyFilters(updatedSearchResults);
        setCombinedResults(results);
        updatePaginationView(results, combinedCurrentPage);
        
      } else if (isSearchMode && hasSorting()) {
        // Re-apply sorting to fresh search results
        const sorted = applySorting(updatedSearchResults, sortField!, sortOrder);
        setCombinedResults(sorted);
        updatePaginationView(sorted, combinedCurrentPage);
        
      } else if (hasActiveFilters() && hasSorting()) {
        // Re-apply filters and sorting using fresh cache data
        const allOrders = await fetchOrdersForFilter();
        const filtered = applyFilters(allOrders);
        const sorted = applySorting(filtered, sortField!, sortOrder);
        setCombinedResults(sorted);
        updatePaginationView(sorted, combinedCurrentPage);
        
      } else if (hasActiveFilters()) {
        // Re-apply filters only using fresh cache data
        const allOrders = await fetchOrdersForFilter();
        const results = applyFilters(allOrders);
        setFilterResults(results);
        updatePaginationView(results, filterCurrentPage);
        
      } else if (hasSorting()) {
        // Re-apply sorting only using fresh cache data
        const allOrders = await fetchOrdersForSort();
        const sorted = applySorting(allOrders, sortField!, sortOrder);
        setSortResults(sorted);
        updatePaginationView(sorted, sortCurrentPage);
        
      } else if (isSearchMode) {
        // Just search mode - update displayed orders using fresh data
        updatePaginationView(updatedSearchResults, searchCurrentPage);
      }
    };
    
    // Use setTimeout to ensure all state updates have completed first
    setTimeout(() => updateDisplayedOrders(), 0);
  }, [
    isFilterMode, isSortMode, isCombinedMode, isSearchMode, hasActiveFilters, hasSorting, 
    applyFilters, applySorting, searchResults, filterCache, sortCache, 
    sortField, sortOrder, pageSize, combinedCurrentPage, filterCurrentPage, sortCurrentPage, 
    searchCurrentPage, filterResults, sortResults, combinedResults, searchCache,
    fetchOrdersForFilter, fetchOrdersForSort, serverOrders
  ]);

  // Remove order from cache
  const removeOrderFromCache = useCallback((orderId: string) => {
    console.log(`Removing order ${orderId} from caches`);
    
    // Helper function to remove an order from an array
    const removeOrderFromArray = (orders: Order[]) => 
      orders.filter(order => order.id !== orderId);
    
    // Helper function to remove order from cache maps
    const removeFromCacheMap = (cacheMap: Map<string, SearchOrdersResult>) => {
      const newCache = new Map(cacheMap);
      for (const [key, cachedResult] of newCache.entries()) {
        const filteredOrders = removeOrderFromArray(cachedResult.orders);
        newCache.set(key, { 
          ...cachedResult, 
          orders: filteredOrders,
          total: filteredOrders.length 
        });
      }
      return newCache;
    };
    
    // Remove from all order arrays
    setOrders(prev => removeOrderFromArray(prev));
    setServerOrders(prev => removeOrderFromArray(prev));
    setSearchResults(prev => removeOrderFromArray(prev));
    setFilterResults(prev => removeOrderFromArray(prev));
    setSortResults(prev => removeOrderFromArray(prev));
    setCombinedResults(prev => removeOrderFromArray(prev));
    
    // Remove from all cache maps
    setSearchCache(prev => removeFromCacheMap(prev));
    setFilterCache(prev => removeFromCacheMap(prev));
    setSortCache(prev => removeFromCacheMap(prev));
    
    // Update total count (subtract 1 for the removed order)
    setTimeout(() => {
      setTotalCount(prev => Math.max(0, prev - 1));
    }, 0);
  }, []);

  return {
    orders,
    loading,
    error,
    totalCount,
    currentPage,
    totalPages,
    hasMore,
    searchQuery,
    selectedStatuses,
    selectedPaymentStatuses,
    selectedShippingMethods,
    selectedDiscountCodes,
    selectedPackedStatuses,
    priorityProcessingFilter,
    sortField,
    sortOrder,
    isSearchMode,
    isFilterMode,
    isSortMode: sortField !== null,
    isCombinedMode,
    searchLoading,
    filterLoading,
    sortLoading,
    setSearchQuery: handleSetSearchQuery,
    setSelectedStatuses: handleSetSelectedStatuses,
    setSelectedPaymentStatuses: handleSetSelectedPaymentStatuses,
    setSelectedShippingMethods: handleSetSelectedShippingMethods,
    setSelectedDiscountCodes: handleSetSelectedDiscountCodes,
    setSelectedPackedStatuses: handleSetSelectedPackedStatuses,
    setPriorityProcessingFilter: handleSetPriorityProcessingFilter,
    setSorting: handleSetSorting,
    handleSearch,
    fetchNextPage,
    fetchPage,
    refresh,
    reset,
    updateOrderInCache,
    removeOrderFromCache,
    invalidateCache,
  };
}; 